/**
 * Farbmathematik für das Branding: sRGB ↔ OKLCH plus WCAG-Kontrast.
 *
 * OKLCH, weil dort L (perzeptuelle Helligkeit), C (Buntheit) und H (Farbton)
 * unabhängig verstellbar sind — genau das braucht die Ableitung „eine
 * Markenfarbe → volle Skala": L normalisieren, ohne den Farbton zu kippen.
 * Formeln: Björn Ottosson (OKLab) und WCAG 2.x (Relativluminanz/Kontrast).
 */

export interface Oklch {
  /** Perzeptuelle Helligkeit 0..1. */
  l: number;
  /** Buntheit ≥ 0 (sRGB-Grenzen je nach Farbton ≈ 0.04–0.32). */
  c: number;
  /** Farbton in Grad 0..360. */
  h: number;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const HEX_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isValidHex(value: string): boolean {
  return HEX_PATTERN.test(value);
}

/** #abc → #aabbcc; wirft bei allem, was kein 3-/6-stelliges Hex ist. */
export function normalizeHex(value: string): string {
  const hex = value.trim().toLowerCase();
  if (!HEX_PATTERN.test(hex)) {
    throw new Error(`Keine gültige Hex-Farbe: "${value}"`);
  }
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return hex;
}

function hexToRgb(hex: string): Rgb {
  const value = normalizeHex(hex);
  const n = parseInt(value.slice(1), 16);
  return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const byte = (channel: number): string =>
    Math.round(Math.min(1, Math.max(0, channel)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${byte(r)}${byte(g)}${byte(b)}`;
}

/** Hex + Alpha (0..1) → 8-stelliges Hex, z. B. für accent-soft. */
export function withAlpha(hex: string, alpha: number): string {
  const byte = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `${normalizeHex(hex)}${byte}`;
}

const srgbToLinear = (c: number): number =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

const linearToSrgb = (c: number): number =>
  c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;

function linearRgb(hex: string): Rgb {
  const { r, g, b } = hexToRgb(hex);
  return { r: srgbToLinear(r), g: srgbToLinear(g), b: srgbToLinear(b) };
}

export function hexToOklch(hex: string): Oklch {
  const { r, g, b } = linearRgb(hex);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const c = Math.hypot(a, bb);
  const h = c < 1e-6 ? 0 : ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360;
  return { l: L, c, h };
}

/** Ohne Gamut-Prüfung — Kanäle können außerhalb [0,1] liegen. */
function oklchToLinearRgb({ l: L, c, h }: Oklch): Rgb {
  const rad = (h * Math.PI) / 180;
  const a = c * Math.cos(rad);
  const bb = c * Math.sin(rad);
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * bb, 3);
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * bb, 3);
  const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * bb, 3);
  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  };
}

function inGamut(rgb: Rgb): boolean {
  const eps = 1e-6;
  return (
    rgb.r >= -eps &&
    rgb.r <= 1 + eps &&
    rgb.g >= -eps &&
    rgb.g <= 1 + eps &&
    rgb.b >= -eps &&
    rgb.b <= 1 + eps
  );
}

/**
 * OKLCH → Hex mit Gamut-Mapping: liegt die Farbe außerhalb von sRGB, wird
 * die Buntheit reduziert (L und H bleiben) — so kippt weder Helligkeit
 * (Kontrastgarantie!) noch Farbton.
 */
export function oklchToHex(color: Oklch): string {
  let target = color;
  if (!inGamut(oklchToLinearRgb(target))) {
    let lo = 0;
    let hi = color.c;
    for (let i = 0; i < 30; i += 1) {
      const mid = (lo + hi) / 2;
      if (inGamut(oklchToLinearRgb({ ...color, c: mid }))) lo = mid;
      else hi = mid;
    }
    target = { ...color, c: lo };
  }
  const { r, g, b } = oklchToLinearRgb(target);
  return rgbToHex({ r: linearToSrgb(r), g: linearToSrgb(g), b: linearToSrgb(b) });
}

/** WCAG-Relativluminanz 0..1. */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = linearRgb(hex);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG-Kontrastverhältnis 1..21 (AA für normalen Text: ≥ 4.5). */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  const [dark, light] = a < b ? [a, b] : [b, a];
  return (light + 0.05) / (dark + 0.05);
}
